import { AfterViewInit, Component, ElementRef, HostListener, Renderer2, ViewChild } from '@angular/core'
import { RouterModule } from '@angular/router'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { CommonModule } from '@angular/common'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDividerModule } from '@angular/material/divider'
import { MatSliderModule } from '@angular/material/slider'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatchService } from '../../core/services/matches.service'
import { Team } from '../../core/models/team.model'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatCardModule } from '@angular/material/card'
import { PizarraService } from './services/pizarra.service'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { SnackbarService } from 'src/app/core/services/snackbar.service'

@Component({
    selector: 'pizarra-component',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        RouterModule,
        MatFormFieldModule,
        MatSelectModule,
        MatButtonModule,
        MatSliderModule,
        MatTableModule,
        MatDividerModule,
        ReactiveFormsModule,
        MatCardModule,
        MatInputModule,
        MatAutocompleteModule,
        FormsModule,
        MatTooltipModule
    ],
    templateUrl: 'pizarra.component.html',
    styleUrl: 'pizarra.component.css'
})
export class PizarraComponent implements AfterViewInit {
    @ViewChild('drawingCanvas') canvasRef!: ElementRef<HTMLCanvasElement>
    @ViewChild('tablero') tableroRef!: ElementRef
    @ViewChild('colorPicker') colorPickerInput!: ElementRef

    private ctx!: CanvasRenderingContext2D
        
    drawingMode = 'none'
    color = '#c30913'
    scaleSlider = 2
    isDrawing = false
    loaded = false
    animationFinished = false
    isDragging = false
    draggedElement: HTMLElement | null = null;
    startX = 0 
    startY = 0
    offsetX = 0
    offsetY = 0
    drawings: Drawing[] = []
    createdElements: HTMLElement[] = []

    players: String[] = []
    filteredPlayers: String[] = []
    teams: any[] = []
    filteredTeams: any[] = []

    pizarra = new FormGroup({
        selectPlayer: new FormControl(''),
        selectElement: new FormControl(''),
        selectBall: new FormControl(''),
        selectTeam: new FormControl<Team | null>(null)
    })

    private originalDimensions = { width: 0, height: 0 }

    constructor(
        private renderer: Renderer2, 
        private snackbar: SnackbarService,
        public service: MatchService,
        public pizarraService: PizarraService
    ) {}

    ngAfterViewInit() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
          //console.warn('renderImage called in SSR, skipping')
          return
        }
        
        this.reloadData()
        this.service.fetchTeamsList().subscribe(val=>{
            this.teams = val as Team[]
            this.players = [...PLAYERS, ...B_PLAYERS]
        })

        this.pizarra.get('selectPlayer')?.valueChanges.subscribe(val=>{
            this.addPlayer()
        })
        this.pizarra.get('selectElement')?.valueChanges.subscribe(val=>{
            this.addElement()
        })
        this.pizarra.get('selectBall')?.valueChanges.subscribe(val=>{
            this.addBall()
        })
            
        setTimeout(() => {
            this.animationFinished = true
        }, 12000)

        setTimeout(() => {
            this.loaded = true
        }, 7000)

        setTimeout(() => {
            const canvas = this.canvasRef.nativeElement
            this.ctx = canvas.getContext('2d')!

            canvas.addEventListener('click', (e) => {
                if (this.drawingMode != 'erase') return
                const rect = canvas.getBoundingClientRect()
                const x = e.clientX - rect.left
                const y = e.clientY - rect.top
            
                this.deleteElement(x, y)
            })
            
            canvas.addEventListener('mousedown', (e) => {
                this.isDrawing = true
                var rect = canvas.getBoundingClientRect()
                this.startX = e.clientX - rect.left
                this.startY = e.clientY - rect.top
            })
            
            canvas.addEventListener('mousemove', (e) => {
                if (!this.isDrawing) return

                var rect = canvas.getBoundingClientRect()
                const x = e.clientX - rect.left
                const y = e.clientY - rect.top
                this.ctx.lineWidth = 2
                this.ctx.strokeStyle = this.color
                this.redrawCanvas()
                
                if (this.drawingMode === 'arrow') {
                    this.drawArrow(this.startX, this.startY, x, y, [0,0])
                } else if (this.drawingMode === 'circle') {
                    const radius = Math.sqrt(Math.pow(x - this.startX, 2) + Math.pow(y - this.startY, 2))
                    this.drawCircle(this.startX, this.startY, radius)
                }else if (this.drawingMode === 'line') {
                    this.drawLine(this.startX, this.startY, x, y)
                }
            })
            
            canvas.addEventListener('mouseup', (e) => {
                if (this.isDrawing) {
                    var rect = canvas.getBoundingClientRect()
                    const endX = e.clientX - rect.left
                    const endY = e.clientY - rect.top
                    this.saveDrawing(this.drawingMode, { x: this.startX, y: this.startY }, { x: endX, y: endY }, this.color)
                }
                this.isDrawing = false
            })

            this.originalDimensions = {
                width: this.tableroRef.nativeElement.offsetWidth,
                height: this.tableroRef.nativeElement.offsetHeight
            }
            this.onResize()
            window.addEventListener('resize', this.onResize.bind(this))
        }, 8000)
    }
      
    private onResize() {
        const tablero = this.tableroRef.nativeElement
        const scaleX = tablero.offsetWidth / this.originalDimensions.width
        const scaleY = tablero.offsetHeight / this.originalDimensions.height
      
        this.createdElements.forEach(element => {
          const left = parseFloat(element.style.left) * scaleX
          const top = parseFloat(element.style.top) * scaleY
      
          this.renderer.setStyle(element, 'left', `${left}px`)
          this.renderer.setStyle(element, 'top', `${top}px`)
        })

        // Scale drawings proportionally
        this.drawings = this.drawings.map(drawing => ({
            ...drawing,
            start: { x: drawing.start.x * scaleX, y: drawing.start.y * scaleY },
            end: { x: drawing.end.x * scaleX, y: drawing.end.y * scaleY }
        }))
    
        this.originalDimensions.width = tablero.offsetWidth
        this.originalDimensions.height = tablero.offsetHeight
      
        this.resizeCanvas()
        this.redrawCanvas()
    }

    resizeCanvas() {
        const canvas = this.canvasRef.nativeElement
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        this.redrawCanvas()
    }

    // Metodos de respuesta a los botones
    arrowBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawingMode = 'arrow'
        canvas.style.pointerEvents = 'auto'
    }
    arrowDiscontinuaBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawingMode = 'arrowDiscontinua'
        canvas.style.pointerEvents = 'auto'
    }
    circleBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawingMode = 'circle'
        canvas.style.pointerEvents = 'auto'
    }
    lineBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawingMode = 'line'
        canvas.style.pointerEvents = 'auto'
    }
    eraseBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawingMode = 'erase'
        canvas.style.pointerEvents = 'auto'
    }
    eraseAllBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawings = []
        this.redrawCanvas()
        this.drawingMode = 'none'
        canvas.style.pointerEvents = 'none'
    }
    drawModeBtnClick(){
        const canvas = this.canvasRef.nativeElement
        this.drawingMode = 'none'
        canvas.style.pointerEvents = 'none'
    }
    clearTablero(){
        const tablero = this.tableroRef.nativeElement

        //Vaciar el tablero
        this.eventListeners.forEach(unlisten => unlisten())
        this.eventListeners = []
        tablero.innerHTML = ""
        this.createdElements = []
    }

    addPlayer(){
        if (this.pizarra.get('selectPlayer')?.valid && this.pizarra.get('selectPlayer')?.value != undefined) {
            const selectPlayer = this.pizarra.get('selectPlayer')?.value!

            let isAdded = document.getElementById(selectPlayer)
            if (!isAdded) {
                const uniqueId = selectPlayer + Math.floor(Math.random() * 100000)
                this.createComponent(uniqueId, selectPlayer, "jugador")
            } else console.log('El jugador ya está en el tablero.')
        } else console.log('Selecciona un jugador antes de agregar.')
    }
    addElement(){
        if (this.pizarra.get('selectElement')?.valid && this.pizarra.get('selectElement')?.value != undefined) {
            const selectElement = this.pizarra.get('selectElement')?.value!

            this.createComponent(selectElement, selectElement, "elemento")
            this.pizarra.get('selectElement')?.reset()
        } else console.log('Selecciona un elemento antes de agregar.')
    }
    addBall(){
        if (this.pizarra.get('selectBall')?.valid && this.pizarra.get('selectBall')?.value != undefined) {
            const selectBall = this.pizarra.get('selectBall')?.value!

            let isAdded = document.getElementById(selectBall)
            if (!isAdded) this.createComponent(selectBall, selectBall, "balon") 
            else console.log('El balón ya está en el tablero.') 
            
            this.pizarra.get('selectBall')?.reset() 
        } else console.log('Selecciona un balón antes de agregar.') 
    }

    addTeam(event: Event){
        event.stopPropagation()
        if(this.pizarra.get('selectTeam')?.valid && this.pizarra.get('selectTeam')?.value != undefined){
            const team = this.pizarra.get('selectTeam')?.value
            const uniqueId = team!.name + Math.floor(Math.random() * 100000)
            this.createChapa(uniqueId, team!)
        }
    }

    filterTeam(inputElement: HTMLInputElement) {
      const inputValue = inputElement.value.toLowerCase()
      if(inputValue.length > 2)
      this.filteredTeams = this.teams.filter(m => m.name.toLowerCase().includes(inputValue))
    }
    filterPlayer(inputElement: HTMLInputElement) {
      const inputValue = inputElement.value.toLowerCase()
      this.filteredPlayers = this.players.filter(m => m.toLowerCase().includes(inputValue))
    }
  
    displayFn(value: any) {
      if(value) return value.name
      else return ""
    }

    createComponent(uniqueId:string, name:string, type:string){
        const tablero = this.tableroRef.nativeElement

        const element = this.renderer.createElement('img')
        this.renderer.setAttribute(element, 'src', `assets/pizarra/${name}.png`)
        this.renderer.setAttribute(element, 'alt', name)
        this.renderer.setAttribute(element, 'id', uniqueId)
        this.renderer.addClass(element, type)
        this.renderer.setAttribute(element, 'draggable', 'true')
        this.renderer.setStyle(element, 'left', `${tablero.offsetWidth / 2 - 28}px`)
        this.renderer.setStyle(element, 'top', `${tablero.offsetHeight / 2 - 28}px`)

        this.applyScaleToElement(element, type)

        // Manejar eventos de arrastre
        const mousedown = this.renderer.listen(element, 'mousedown', (event: MouseEvent) => this.onMouseDown(event, element))
        const dragstart = this.renderer.listen(element, 'dragstart', (event: MouseEvent) => event.preventDefault())
        const contextmenu = this.renderer.listen(element, 'contextmenu', (event: MouseEvent) => {
            event.preventDefault()
            tablero.removeChild(element)
            this.createdElements = this.createdElements.filter(e => e != element)
        })

        this.eventListeners.push(mousedown)
        this.eventListeners.push(dragstart)
        this.eventListeners.push(contextmenu)

        this.renderer.appendChild(tablero, element)
        this.createdElements.push(element)
    }

    createChapa(uniqueId: string, team:Team){
        const tablero = this.tableroRef.nativeElement
        
        if(!team.imgUrl) return

        // CHAPA
        const element = this.renderer.createElement('div')
        this.renderer.setStyle(element, 'display', 'flex')
        this.renderer.setStyle(element, 'justifyContent', 'center')
        this.renderer.setStyle(element, 'alignItems', 'center')
        this.renderer.setStyle(element, 'width', '1.2em')
        this.renderer.setStyle(element, 'aspect-ratio', '1/1')
        this.renderer.setStyle(element, 'borderRadius', '100%')
        if(team.name.includes('Atlético de Madrid')) this.renderer.setStyle(element, 'backgroundColor', '#c30913')
        else this.renderer.setStyle(element, 'backgroundColor', '#4464ad')
        this.renderer.setStyle(element, 'overflow', 'hidden')

        // CIRCULO
        const circle = this.renderer.createElement('div')
        this.renderer.setStyle(circle, 'position', 'absolute')
        this.renderer.setStyle(circle, 'width', '90%')
        this.renderer.setStyle(circle, 'aspect-ratio', '1/1')
        this.renderer.setStyle(circle, 'borderRadius', '100%')
        this.renderer.setStyle(circle, 'border', '2px solid #f2f2f2')

        // TEAM BADGE
        const img = this.renderer.createElement('img')
        this.renderer.setAttribute(img, 'src', team.imgUrl as string)
        this.renderer.setAttribute(img, 'alt', team.name)
        this.renderer.setStyle(img, 'width', '65%')
        this.renderer.setStyle(img, 'height', '65%')

        this.renderer.appendChild(element, img)
        this.renderer.appendChild(element, circle)

        this.renderer.setAttribute(element, 'alt', team.name)
        this.renderer.setAttribute(element, 'id', uniqueId)
        this.renderer.addClass(element, "jugador")
        this.renderer.setAttribute(element, 'draggable', 'true')
        this.renderer.setStyle(element, 'left', `${tablero.offsetWidth / 2 - 28}px`)
        this.renderer.setStyle(element, 'top', `${tablero.offsetHeight / 2 - 28}px`)

        this.applyScaleToElement(element, "jugador")

        // Manejar eventos de arrastre
        const mousedown = this.renderer.listen(element, 'mousedown', (event: MouseEvent) => this.onMouseDown(event, element))
        const dragstart = this.renderer.listen(element, 'dragstart', (event: MouseEvent) => event.preventDefault())
        const contextmenu = this.renderer.listen(element, 'contextmenu', (event: MouseEvent) => {
            event.preventDefault()
            tablero.removeChild(element)
            this.createdElements = this.createdElements.filter(e => e != element)
        })

        this.eventListeners.push(mousedown)
        this.eventListeners.push(dragstart)
        this.eventListeners.push(contextmenu)

        this.renderer.appendChild(tablero, element)
        this.createdElements.push(element)
    }

    onMouseDown(event: MouseEvent, element: HTMLElement) {
        this.isDragging = true
        this.draggedElement = element
    
        // Obtener la posición del ratón con respecto a la imagen
        const rect = element.getBoundingClientRect()
        this.offsetX = event.clientX - rect.left
        this.offsetY = event.clientY - rect.top
    
        // Cambiar el cursor a 'grabbing' mientras arrastra
        this.renderer.setStyle(this.draggedElement, 'cursor', 'grabbing')
    }
    
    @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent) {
        if (this.isDragging && this.draggedElement) {
          this.moveElement(event)
        }
    }
    
    @HostListener('document:mouseup')
    onMouseUp() {
        if (this.isDragging) {
          this.isDragging = false
          if (this.draggedElement) {
            // Cambiar el cursor de nuevo a 'grab'
            this.renderer.setStyle(this.draggedElement, 'cursor', 'grab')
            this.draggedElement = null
          }
        }
    }
    
    moveElement(event: MouseEvent) {
        if (!this.draggedElement) return
    
        const tableroRect = this.tableroRef.nativeElement.getBoundingClientRect()
    
        // Calcular la nueva posición dentro del tablero
        let x = event.clientX - tableroRect.left - this.offsetX
        let y = event.clientY - tableroRect.top - this.offsetY
    
        // Restringir el movimiento dentro del tablero
        x = Math.max(0, Math.min(x, tableroRect.width - this.draggedElement.offsetWidth))
        y = Math.max(0, Math.min(y, tableroRect.height - this.draggedElement.offsetHeight))
    
        // Mover la imagen
        this.renderer.setStyle(this.draggedElement, 'left', `${x}px`)
        this.renderer.setStyle(this.draggedElement, 'top', `${y}px`)
    }

    // Función para dibujar una flecha
    drawArrow(fromx: number, fromy: number, tox: number, toy: number, dashArray: number[] | null | undefined) {
        const headlen = 20
        const dx = tox - fromx
        const dy = toy - fromy
        const angle = Math.atan2(dy, dx)
        
        this.ctx.beginPath();
        if(dashArray) this.ctx.setLineDash(dashArray)
        this.ctx.moveTo(fromx, fromy)
        this.ctx.lineTo(tox, toy)
        this.ctx.lineTo(tox - headlen * Math.cos(angle - Math.PI / 6), toy - headlen * Math.sin(angle - Math.PI / 6))
        this.ctx.moveTo(tox, toy)
        this.ctx.lineTo(tox - headlen * Math.cos(angle + Math.PI / 6), toy - headlen * Math.sin(angle + Math.PI / 6))
        this.ctx.stroke()
        this.ctx.setLineDash([])
    }

    // Función para dibujar una linea
    drawLine(fromx: number, fromy: number, tox: number, toy: number) {
        this.ctx.beginPath()
        this.ctx.moveTo(fromx, fromy)
        this.ctx.lineTo(tox, toy)
        this.ctx.lineWidth = 5
        this.ctx.stroke()
    }

    // Función para dibujar un círculo
    drawCircle(x: number, y: number, radius: number) {
        this.ctx.beginPath()
        this.ctx.arc(x, y, radius, 0, 2 * Math.PI)
        this.ctx.stroke()
    }

    // Función para guardar cada dibujo
    saveDrawing(type: string, start: { x: number; y: number }, end: { x: number; y: number }, color: string) {
        this.drawings.push({ type, start, end, color })
        this.redrawCanvas()
    }

    // Función para redibujar todo el canvas
    redrawCanvas() {
        const canvas = this.canvasRef.nativeElement
        this.ctx.clearRect(0, 0, canvas.width, canvas.height)
        this.drawings.forEach((drawing: { color: string | CanvasGradient | CanvasPattern; type: string; start: { x: number; y: number }; end: { x: number; y: number } }) => {
            this.ctx.strokeStyle = drawing.color
            this.ctx.lineWidth = 2
            if (drawing.type === 'arrow') {
                this.drawArrow(drawing.start.x, drawing.start.y, drawing.end.x, drawing.end.y, null)
            } else if (drawing.type === 'circle') {
                const radius = Math.sqrt(Math.pow(drawing.end.x - drawing.start.x, 2) + Math.pow(drawing.end.y - drawing.start.y, 2))
                this.drawCircle(drawing.start.x, drawing.start.y, radius)
            } else if (drawing.type === 'line') {
                this.drawLine(drawing.start.x, drawing.start.y, drawing.end.x, drawing.end.y)
            } else if (drawing.type === 'arrowDiscontinua') {
                this.drawArrow(drawing.start.x, drawing.start.y, drawing.end.x, drawing.end.y, [10, 5])
            }
        })
    }

    // Función para eliminar un dibujo
    deleteElement(x: any, y: any) {
        // Busca si el click fue sobre alguna flecha
        for (let i = 0; i < this.drawings.length; i++) {
            if (this.isPointOnArrow(x, y, this.drawings[i])) {
                // Elimina la flecha del array
                this.drawings.splice(i, 1)
                this.redrawCanvas() // Redibuja el lienzo sin la flecha
                break
            }
        }
    }

    isPointOnArrow(x: number, y: number, arrow: { start: { x: any; y: any }; end: { x: any; y: any } }) {
        const { start: { x: fromx, y: fromy }, end: { x: tox, y: toy } } = arrow
    
        // Distancia punto a línea
        const lengthSquared = (tox - fromx) ** 2 + (toy - fromy) ** 2
        if (lengthSquared === 0) return false // El punto es igual al inicio/final
        const t = ((x - fromx) * (tox - fromx) + (y - fromy) * (toy - fromy)) / lengthSquared
        if (t < 0 || t > 1) return false // El punto proyectado está fuera del segmento
    
        const projX = fromx + t * (tox - fromx)
        const projY = fromy + t * (toy - fromy)
    
        const distance = Math.sqrt((x - projX) ** 2 + (y - projY) ** 2)
        
        // Si la distancia es menor que un umbral, se considera clic en la flecha
        return distance < 50
    }

    // Apply scaling to the created elements based on their type
    applyScaleToElement(element: HTMLElement, type: string) {
        if (type === 'jugador') {
            this.renderer.setStyle(element, 'width', `${2 * this.scaleSlider}em`)
        } else if (type === 'elemento') {
            this.renderer.setStyle(element, 'height', `${4.5 * this.scaleSlider}em`)
        } else if (type === 'balon') {
            this.renderer.setStyle(element, 'width', `${1.5 * this.scaleSlider}em`)
        }
    }

    // Update scaling for all created elements when the scale factor changes
    updateElementScales() {
        this.createdElements.forEach(element => {
        const type = element.classList[0] // Assuming the type is the first class
        this.applyScaleToElement(element, type)
        })
    }

    // Called when the slider value changes
    onScaleChange() {
        this.updateElementScales() // Apply new scaling to all elements
    }

    openColorPicker() {
        this.colorPickerInput.nativeElement.click()
    }

    eventListeners: (() => void)[] = []
    pizarras: MatTableDataSource<any> = new MatTableDataSource<any>()
    displayedColumns = ['name', 'actions']
    populate(config: any){
        const tablero = this.tableroRef.nativeElement

        //Vaciar el tablero
        this.eventListeners.forEach(unlisten => unlisten())
        this.eventListeners = []
        tablero.innerHTML = ""
        this.createdElements = []
        
        //Recuperar la pizarra
        for (let element of config.pizarra){
            const parser = new DOMParser()
            const doc = parser.parseFromString(element, "text/html")
            const elementoRecuperado = doc.body.firstChild

            // Manejar eventos de arrastre
            const mousedown = this.renderer.listen(elementoRecuperado, 'mousedown', (event: MouseEvent) => this.onMouseDown(event, elementoRecuperado as HTMLElement))
            const dragstart = this.renderer.listen(elementoRecuperado, 'dragstart', (event: MouseEvent) => event.preventDefault())
            const contextmenu = this.renderer.listen(elementoRecuperado, 'contextmenu', (event: MouseEvent) => {
                event.preventDefault()
                tablero.removeChild(elementoRecuperado)
                this.createdElements = this.createdElements.filter(e => e != elementoRecuperado)
            })

            this.eventListeners.push(mousedown)
            this.eventListeners.push(dragstart)
            this.eventListeners.push(contextmenu)

            this.renderer.appendChild(tablero, elementoRecuperado)
            this.createdElements.push(elementoRecuperado as HTMLElement)
        }
    }
    save(){
        this.pizarraService.save(this.createdElements)
        .subscribe(val=>{
          this.snackbar.showSnackbar('Pizarra guardada')
          this.reloadData()
        })
    }
    remove(_id: string){
        this.pizarraService.remove(_id)
        .subscribe(res=>{
            this.snackbar.showSnackbar('Deleted successfully')
            this.reloadData()
        })
    }
    reloadData(){
        this.pizarraService.get()
        .subscribe(val=>{
        this.pizarras = new MatTableDataSource(val as any[])
        })
    }
}

interface Drawing {
    type: string;
    start: { x: number, y: number }; 
    end: { x: number, y: number };
    color: string;
  }

const PLAYERS = [
    "Vacio",
    "Azpilicueta",
    "Barrios",
    "Correa",
    "De Paul",
    "Gallagher",
    "Gimenez",
    "Giuliano",
    "Galan",
    "Griezmann",
    "Julian",
    "Koke",
    "Lemar",
    "Lenglet",
    "Le Normand",
    "Lino",
    "Llorente",
    "Molina",
    "Musso",
    "Oblak",
    "Reinildo",
    "Riquelme",
    "Sorloth",
    "Witsel",
    "Cholo"
]

const B_PLAYERS = [
    "Ale",
    "Boñar",
    "Carlos Giménez",
    "Dani Martínez",
    "Dani Muñoz",
    "Diego Bri",
    "Esquivel",
    "Gero Spina",
    "Gismera",
    "Gomis",
    "Iker Luque",
    "Kostis",
    "Iturbe",
    "Joaquín",
    "Julio",
    "Martin Pascual",
    "Mollejo",
    "Jano Monserrate",
    "Adrián Niño",
    "Omar Janneh",
    "Pablo Pérez",
    "Rayane",
    "Salim",
    "Selu Diallo",
    "Serrano",
    "Torres"
]